<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useThemeStore } from '@/stores/themes';

const themeStore = useThemeStore();

const theme_1 = ref(null); // dark side
const theme_2 = ref(null); // light side

const getLuminance = (hex) => {
	if (!hex) return null;

	hex = hex.replace('#', '');

	if (hex.length > 6) hex = hex.substring(0, 6);

	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	if (isNaN(r) || isNaN(g) || isNaN(b)) return null;

	return (0.299 * r + 0.587 * g + 0.114 * b);
};

const getThemeScore = (theme) => {
	let totalLuminance = 0;
	let count = 0;

	for (const colorValue of Object.values(theme.colors)) {
		if (typeof colorValue === 'string' && colorValue.startsWith('#')) {
			const luminance = getLuminance(colorValue);

			if (luminance !== null) {
				totalLuminance += luminance;
				count++;
			}
		}
	}

	return count === 0 ? 0 : (totalLuminance / count);
};

onBeforeMount(() => {
	if (themeStore.themes.length >= 2) {
		const theme_a = themeStore.themes[0];
		const theme_b = themeStore.themes[1];

		const score_a = getThemeScore(theme_a);
		const score_b = getThemeScore(theme_b);

		if (score_a < score_b) {
			theme_1.value = theme_a.id;
			theme_2.value = theme_b.id;
		} else {
			theme_1.value = theme_b.id;
			theme_2.value = theme_a.id;
		}
	}
});

const is_dark_mode = computed(() => {
	return themeStore.selected_theme === theme_1.value;
});

const toggleTheme = () => {
	if (is_dark_mode.value) themeStore.enableTheme(theme_2.value);
	else themeStore.enableTheme(theme_1.value);
};
</script>

<template>
	<button class="switch_container" @click="toggleTheme" aria-label="Toggle Dark Mode" :aria-checked="is_dark_mode" role="switch">

		<div class="switch_track" :class="{ 'switch_track--active': is_dark_mode }">

			<span class="icon_wrapper sun_icon">
				<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
				<circle cx="12" cy="12" r="5"></circle>
				<line x1="12" y1="1" x2="12" y2="3"></line>
				<line x1="12" y1="21" x2="12" y2="23"></line>
				<line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
				<line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
				<line x1="1" y1="12" x2="3" y2="12"></line>
				<line x1="21" y1="12" x2="23" y2="12"></line>
				<line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
				<line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
				</svg>
			</span>

			<span class="icon_wrapper moon_icon">
				<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
				<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
				</svg>
			</span>

			<div class="switch_thumb"></div>
		</div>
	</button>
</template>

<style scoped>
.switch_container {
	background: transparent;
	border: none;
	cursor: pointer;
	padding: 0;
	outline: none;
	margin: 0 10px;
}

@media screen and (min-width: 992px) and (max-width: 1150px) {
	.switch_container {
		display: none;
	}
}

/* Track size reduced */
.switch_track {
	width: 42px;
	height: 22px;
	background-color: var(--dark-gray);
	border: 1px solid var(--tab-gray);
	border-radius: 999px;
	position: relative;
	transition: all 0.3s ease;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 4px;
}

.icon_wrapper {
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--white);
	z-index: 1;
	opacity: 0.8;
}

.switch_track--active {
	background-color: var(--dark-gray);
	border-color: var(--tab-gray);
}

/* Thumb size reduced */
.switch_thumb {
	width: 16px;
	height: 16px;
	border-radius: 50%;
	background: var(--primary);
	position: absolute;
	top: 50%;
	left: 2px;
	transform: translateY(-50%);
	transition: transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
	z-index: 2;
}

/* Adjusted translation distance for smaller width */
.switch_track--active .switch_thumb {
	transform: translate(20px, -50%);
}
</style>